import React, { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserBasicsStep from './UserBasicsStep';
import SportSelectionStep from './SportSelectionStep';
import { API_BASE } from '../../config';
import StravaConnectButton from '../Auth/StravaConnectButton';
import { useAuth } from '../Auth/AuthContext';

import GenerationProgress from './PlanGeneration/GenerationProgress';
import MacroPreview from './PlanGeneration/MacroPreview';
import TipsCarousel from './PlanGeneration/TipsCarousel';
import { selectTips } from './PlanGeneration/tips';

export default function Wizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { loading: authLoading, user } = useAuth();
  const [step, setStep] = useState('user_basics'); // user_basics, sport_selection, persona_selection, intake, event, feasibility_check
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [intakeConfig, setIntakeConfig] = useState(null);
  const [eventConfig, setEventConfig] = useState(null);

  // Data State
  const [runnerProfile, setRunnerProfile] = useState({});
  const [trainingGoal, setTrainingGoal] = useState({});
  const [feasibilityReport, setFeasibilityReport] = useState(null);
  const [selectedModification, setSelectedModification] = useState(null);

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [authError, setAuthError] = useState('');

  // Generation State
  const [generatedPlanId, setGeneratedPlanId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [partialPlanJson, setPartialPlanJson] = useState(null);

  const selectedTips = useMemo(() => {
    return selectTips({
      sport: runnerProfile?.sport || 'run',
      personaId: runnerProfile?.persona_id,
      goalPrimary: trainingGoal?.goal_primary,
      eventDistanceType: trainingGoal?.event_type
    });
  }, [runnerProfile?.sport, runnerProfile?.persona_id, trainingGoal?.goal_primary, trainingGoal?.event_type]);

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm();

  const watchedFields = watch();

  const stravaDefaults = useMemo(() => {
    const athlete = user?.strava_athlete_json;
    if (!athlete) return {};

    const firstName = String(athlete.firstname || '').trim();
    const lastName = String(athlete.lastname || '').trim();
    const username = String(athlete.username || '').trim();

    const displayName = `${firstName} ${lastName}`.trim() || username || '';

    const rawSex = String(athlete.sex || athlete.gender || '').trim().toUpperCase();
    let sex = '';
    if (rawSex === 'M' || rawSex === 'MALE') sex = 'male';
    else if (rawSex === 'F' || rawSex === 'FEMALE') sex = 'female';

    const w = athlete.weight;
    const weight_kg = typeof w === 'number' && Number.isFinite(w) && w > 0 ? w : null;

    // Strava does not expose age/birthdate in the standard athlete payload.
    return {
      display_name: displayName,
      sex,
      weight_kg
    };
  }, [user]);

  useEffect(() => {
    // Fetch personas when entering persona selection (sport-dependent).
    if (step !== 'persona_selection') return;
    const sport = runnerProfile?.sport || 'run';
    fetch(`${API_BASE}/wizard/personas?lang=${i18n.language}&sport=${sport}`)
      .then(res => res.json())
      .then(data => setPersonas(data))
      .catch(err => console.error('Failed to fetch personas', err));
  }, [i18n.language, runnerProfile?.sport, step]);

  // Restore form state when navigating between steps
  useEffect(() => {
    if (step === 'intake') {
      reset(runnerProfile);
    } else if (step === 'event') {
      reset(trainingGoal);
    }
  }, [step, reset, runnerProfile, trainingGoal]);

  const onSubmitUserBasics = async (data) => {
    // Persist language choice early
    if (data.preferred_language && data.preferred_language !== i18n.language) {
      await i18n.changeLanguage(data.preferred_language);
      localStorage.setItem('i18nextLng', data.preferred_language);
    }

    setRunnerProfile(prev => ({ ...prev, ...data }));
    setStep('sport_selection');
  };

  const onBackFromSportSelection = () => {
    setStep('user_basics');
  };

  const onNextFromSportSelection = ({ sport, visibility }) => {
    setRunnerProfile(prev => ({ ...prev, sport, visibility }));
    setStep('persona_selection');
  };

  const handlePersonaPick = (personaId) => {
    setSelectedPersona(personaId);
    setRunnerProfile(prev => ({ ...prev, persona_id: personaId }));
  };

  const handlePersonaNext = () => {
    const personaId = selectedPersona;
    if (!personaId) return;
    fetch(`${API_BASE}/wizard/questions/${personaId}?lang=${i18n.language}`)
      .then(res => res.json())
      .then(data => {
        setIntakeConfig(data);
        // reset() is handled by the useEffect on step change
        setStep('intake');
      });
  };

  const handlePersonaBack = () => {
    setStep('sport_selection');
  };

  const onSubmitIntake = (data) => {
    setRunnerProfile(prev => ({ ...prev, ...data }));

    const sport = runnerProfile?.sport || 'run';
    fetch(`${API_BASE}/wizard/event-questions?lang=${i18n.language}&sport=${sport}`)
      .then(res => res.json())
      .then(data => {
        setEventConfig(data);
        // reset() is handled by the useEffect on step change
        setStep('event');
      });
  };

  const handleIntakeBack = () => {
    setStep('persona_selection');
  };

  const onSubmitEvent = (data) => {
    setTrainingGoal(data);
    const combinedProfileForRules = { ...runnerProfile, ...data };

    fetch(`${API_BASE}/wizard/validate?lang=${i18n.language}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(combinedProfileForRules)
    })
    .then(res => res.json())
    .then(validation => {
        if (!validation.valid) {
            alert("Safety block: " + validation.report.messages.join("\n"));
            return;
        }

        setIsAnalyzing(true);
        setStep('feasibility_check');

        fetch(`${API_BASE}/wizard/analyze-feasibility?lang=${i18n.language}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                profile: runnerProfile,
                goal: data,
                lang: i18n.language
            })
        })
        .then(res => res.json())
        .then(analysis => {
            setFeasibilityReport(analysis);
            if (analysis.status === 'feasible' && analysis.suggestions.length > 0) {
                 setSelectedModification(analysis.suggestions[0]);
            }
        })
        .finally(() => setIsAnalyzing(false));
    });
  };

  const handleEventBack = () => {
    setStep('intake');
  };

  const handleFeasibilityBack = () => {
    setStep('event');
  };

  useEffect(() => {
    if (!generatedPlanId) return;

    const poll = async () => {
        try {
            const res = await fetch(`${API_BASE}/plans/${generatedPlanId}`, { credentials: 'include' });
            if (!res.ok) return;
            const data = await res.json();

            setGenerationStatus(data.status);
            // The API returns the plan JSON in the 'plan' property
            if (data.plan) {
                setPartialPlanJson(data.plan);
            }

            // If we reached generating_audit (Step 3 start) or active, we can redirect.
            // Ideally redirect on generating_audit so user sees the workouts immediately.
            if (data.status === 'generating_audit' || data.status === 'active') {
                navigate(`/plans/${generatedPlanId}`);
            }
        } catch (e) {
            console.error("Polling error", e);
        }
    };

    // Poll frequently
    const interval = setInterval(poll, 3000);
    poll(); // immediate
    return () => clearInterval(interval);
  }, [generatedPlanId, navigate]);

  const handleStartGeneration = () => {
      const modifications = selectedModification ? selectedModification.modifications : {};

      setStep('generating');
      setGenerationStatus('generating_macro');
      setPartialPlanJson(null); // Clear previous plan preview if any

      fetch(`${API_BASE}/wizard/generate-plan?lang=${i18n.language}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
              profile: runnerProfile,
              goal: trainingGoal,
              modifications: modifications,
              eligibility_report: feasibilityReport
          })
      })
      .then(async (res) => {
          if (res.status === 401) {
            setAuthError('You must connect with Strava before creating a plan.');
            setStep('feasibility_check'); // Go back to show error
            return null;
          }
          return await res.json();
      })
      .then(data => {
          if (!data) return;
          if (data.planId) {
            setGeneratedPlanId(data.planId);
            // Polling useEffect will handle the rest
          } else {
             console.error("No plan ID returned");
             setStep('feasibility_check');
          }
      })
      .catch(err => {
          console.error("Plan generation init failed", err);
          setStep('feasibility_check');
      });
  };

  // --- Render Helpers (Field rendering omitted for brevity, same as before) ---
  const shouldShowQuestion = (question) => {
      if (!question.ask_if) return true;
      return Object.entries(question.ask_if).every(([key, value]) => {
          const actualValue = watchedFields[key];
          if (Array.isArray(value)) return value.includes(actualValue);
          if (typeof value === 'boolean') return String(actualValue) === String(value);
          return actualValue === value;
      });
  };

  const isOptionalQuestion = (q) => {
    // Prefer explicit metadata if present
    if (q?.optional === true) return true;
    if (q?.required === false) return true;

    // Backward-compatible fallback: some models encode optionality only in the label
    const label = String(q?.label || '');
    return /\boptional\b|\boptionnel\b/i.test(label);
  };

  const renderField = (q) => {
      if (!shouldShowQuestion(q)) return null;
      const optional = isOptionalQuestion(q);

      return (
          <div key={q.id} className="mb-6">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{q.label}</label>

              {q.type === 'single_choice' && (
                  <div className="relative">
                    <select
                      {...register(q.id, { required: !optional })}
                      className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium appearance-none"
                    >
                        <option value="">{t('wizard.user_basics.placeholders.select')}</option>
                        {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
              )}

              {q.type === 'multi_choice' && (
                   <div className="flex flex-col gap-3">
                       {q.options.map(opt => (
                           <label key={opt} className="inline-flex items-center p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-indigo-200 hover:shadow-sm transition-all cursor-pointer">
                               <input
                                type="checkbox"
                                value={opt}
                                {...register(q.id, {
                                  validate: (v) => {
                                    if (optional) return true;
                                    return Array.isArray(v) ? v.length > 0 : Boolean(v);
                                  }
                                })}
                                className="mr-3 h-5 w-5 text-indigo-600 rounded-md border-gray-300 focus:ring-indigo-500 bg-white"
                               />
                               <span className="text-gray-700 font-medium">{opt}</span>
                           </label>
                       ))}
                   </div>
              )}

              {q.type === 'boolean' && (
                  <div className="flex gap-4">
                      <label className="inline-flex items-center p-3 px-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-indigo-200 hover:shadow-sm transition-all cursor-pointer">
                          <input
                            type="radio"
                            value="true"
                            {...register(q.id, { required: !optional })}
                            className="mr-3 h-5 w-5 text-indigo-600 border-gray-300 focus:ring-indigo-500 bg-white"
                          />
                          <span className="font-bold text-gray-700">{t('wizard.plans_table.yes')}</span>
                      </label>
                      <label className="inline-flex items-center p-3 px-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:border-indigo-200 hover:shadow-sm transition-all cursor-pointer">
                          <input
                            type="radio"
                            value="false"
                            {...register(q.id, { required: !optional })}
                            className="mr-3 h-5 w-5 text-indigo-600 border-gray-300 focus:ring-indigo-500 bg-white"
                          />
                          <span className="font-bold text-gray-700">{t('wizard.plans_table.no')}</span>
                      </label>
                  </div>
              )}

              {(q.type === 'string' || q.type === 'number' || q.type === 'date') && (
                  <input
                    type={q.type === 'string' ? 'text' : q.type}
                    {...register(q.id, { required: !optional })}
                    className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
                  />
              )}

               {q.type === 'object' && q.fields && (
                   <div className="pl-4 border-l-2 border-indigo-100 space-y-4">
                       {q.fields.map(subQ => (
                           <div key={subQ.id}>
                               <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{subQ.label}</label>
                               <input
                                    type={subQ.type === 'number' ? 'number' : 'text'}
                                    {...register(`${q.id}.${subQ.id}`)}
                                    className="block w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none font-medium placeholder:text-gray-400"
                               />
                           </div>
                       ))}
                   </div>
               )}

              {errors?.[q.id] ? (
                <div className="text-sm text-red-600 mt-2 font-medium bg-red-50 p-2 rounded-lg border border-red-100">{t('wizard.user_basics.errors.required')}</div>
              ) : null}
          </div>
      );
  };

  // Require login before starting the wizard to avoid losing progress mid-way.
  if (authLoading) {
    return (
      <div className="max-w-2xl mx-auto p-12 bg-white shadow-lg rounded-lg mt-10 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h3 className="text-xl font-bold text-gray-700">Checking login…</h3>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white shadow-lg rounded-lg mt-10">
        <h2 className="text-2xl font-bold mb-3 text-gray-800">{t('wizard.connect_strava')}</h2>
        <p className="text-gray-600 mb-6">
          {t('wizard.connect_strava_desc')}
        </p>
        <StravaConnectButton
          redirectPath="/wizard"
          className="bg-orange-600 text-white font-bold py-3 px-5 rounded-lg shadow hover:bg-orange-700 transition"
        />
      </div>
    );
  }

  // --- Views ---

  if (step === 'user_basics') {
    return (
      <UserBasicsStep
        defaultValues={{ ...stravaDefaults, ...runnerProfile }}
        onSubmit={onSubmitUserBasics}
      />
    );
  }

  if (step === 'sport_selection') {
    return (
      <SportSelectionStep
        initialSport={runnerProfile?.sport || null}
        initialVisibility={runnerProfile?.visibility || null}
        onBack={onBackFromSportSelection}
        onNext={onNextFromSportSelection}
      />
    );
  }

  if (step === 'persona_selection') {
    const canContinue = Boolean(selectedPersona);
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-2 md:mt-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">{t('wizard.select_persona')}</h2>
        <div className="grid grid-cols-1 gap-4">
          {personas.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePersonaPick(p.id)}
              className={[
                "p-4 border-2 rounded-lg text-left transition duration-200 bg-gray-50 hover:bg-blue-50 cursor-pointer",
                selectedPersona === p.id ? "border-blue-500" : "border-gray-100 hover:border-blue-500"
              ].join(' ')}
            >
              <span className="font-semibold text-lg text-gray-900">{p.label}</span>
              {p.description ? (
                <div className="text-sm text-gray-600 mt-1">{p.description}</div>
              ) : null}
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handlePersonaBack}
            className="px-6 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition cursor-pointer"
          >
            {t('wizard.previous')}
          </button>

          <button
            type="button"
            disabled={!canContinue}
            onClick={handlePersonaNext}
            className={[
              "px-6 py-3 rounded-lg font-semibold transition cursor-pointer",
              canContinue ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"
            ].join(' ')}
          >
            {t('wizard.next')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'intake' && intakeConfig) {
      return (
          <div className="max-w-2xl mx-auto p-8 bg-white shadow-2xl shadow-indigo-100/50 rounded-3xl mt-4 md:mt-10 border border-gray-100">
              <h2 className="text-3xl font-black mb-6 text-gray-900 tracking-tight">{t('wizard.title')}</h2>
              <form onSubmit={handleSubmit(onSubmitIntake)} className="space-y-6">
                  {intakeConfig.questions.map(q => renderField(q))}
                  <div className="mt-10 flex items-center justify-between gap-4 pt-6 border-t border-gray-50">
                    <button
                      type="button"
                      onClick={handleIntakeBack}
                      className="px-6 py-3 rounded-full font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors uppercase tracking-wide text-sm"
                    >
                      {t('wizard.previous')}
                    </button>

                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-8 py-3.5 rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 font-bold text-sm tracking-wide uppercase"
                    >
                      {t('wizard.next')}
                    </button>
                  </div>
              </form>
          </div>
      );
  }

  if (step === 'event' && eventConfig) {
      return (
          <div className="max-w-2xl mx-auto p-8 bg-white shadow-2xl shadow-indigo-100/50 rounded-3xl mt-4 md:mt-10 border border-gray-100">
              <h2 className="text-3xl font-black mb-6 text-gray-900 tracking-tight">{t('wizard.subtitle')}</h2>
              <form onSubmit={handleSubmit(onSubmitEvent)} className="space-y-6">
                  {eventConfig.questions.map(q => renderField(q))}
                  <div className="mt-10 flex items-center justify-between gap-4 pt-6 border-t border-gray-50">
                    <button
                      type="button"
                      onClick={handleEventBack}
                      className="px-6 py-3 rounded-full font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors uppercase tracking-wide text-sm"
                    >
                      {t('wizard.previous')}
                    </button>

                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-8 py-3.5 rounded-full shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 hover:bg-indigo-700 transition-all duration-200 font-bold text-sm tracking-wide uppercase"
                    >
                      {t('wizard.analyzing')}
                    </button>
                  </div>
              </form>
          </div>
      );
  }

  if (step === 'feasibility_check') {
      if (isAnalyzing || !feasibilityReport) {
          return (
              <div className="max-w-2xl mx-auto p-12 bg-white shadow-lg rounded-lg mt-2 md:mt-10 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <h3 className="text-xl font-bold text-gray-700">{t('wizard.analyzing')}</h3>
              </div>
          );
      }

      const statusColors = {
          feasible: 'bg-green-50 border-green-500 text-green-800',
          risky: 'bg-yellow-50 border-yellow-500 text-yellow-800',
          unrealistic: 'bg-red-50 border-red-500 text-red-800'
      };

      return (
          <div className="max-w-3xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-2 md:mt-10">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">{t('modals.coach_analysis')}</h2>

              {authError ? (
                <div className="mb-6 p-4 rounded-lg border border-orange-200 bg-orange-50 text-orange-900">
                  <div className="font-bold mb-2">{authError}</div>
                  <StravaConnectButton
                    redirectPath="/wizard"
                    className="bg-orange-600 text-white font-bold py-2 px-4 rounded-lg shadow hover:bg-orange-700 transition"
                  />
                </div>
              ) : null}

              <div className={`p-6 mb-8 rounded-lg border-l-4 ${statusColors[feasibilityReport.status] || 'bg-gray-50'}`}>
                  <h3 className="font-bold text-xl mb-2 capitalize">{t(`wizard.${feasibilityReport.status}`) || feasibilityReport.status}</h3>
                  <p className="text-lg">{feasibilityReport.analysis}</p>
              </div>

              <h4 className="font-bold text-lg mb-4 text-gray-700">{t('modals.coach_suggestions')}</h4>
              <div className="grid gap-4 mb-8">
                  {feasibilityReport.suggestions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSelectedModification(option)}
                        className={`p-4 border-2 rounded-lg text-left transition cursor-pointer ${
                            selectedModification?.id === option.id
                            ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                          <div className="font-bold text-blue-900">{option.label}</div>
                          <div className="text-gray-600 text-sm mt-1">{option.description}</div>
                      </button>
                  ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleFeasibilityBack}
                  className="px-6 py-3 rounded-lg font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition cursor-pointer"
                >
                  {t('wizard.previous')}
                </button>

                <button
                  type="button"
                  onClick={handleStartGeneration}
                  disabled={!selectedModification}
                  className={`px-8 py-3 rounded-lg font-bold text-lg shadow transition cursor-pointer ${
                    !selectedModification
                      ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {t('wizard.generate')}
                </button>
              </div>
          </div>
      );
  }

    if (step === 'generating') {
        const isWorkouts = generationStatus === 'generating_workouts';

        // Texts
        let statusLabel = t('wizard.generation.step1'); // "Designing your macro-cycle..."
        let duration = 60;

        if (isWorkouts) {
            statusLabel = t('wizard.generation.step2'); // "Crafting every single workout..."
            duration = 180;
        }

        return (
            <div className="max-w-3xl mx-auto p-4 md:p-6 bg-white shadow-lg rounded-lg mt-2 md:mt-10 flex flex-col items-center justify-center min-h-[500px]">

                {/* 1) Progress Bar - slightly more compact */}
                <div className="w-full mb-6">
                   <GenerationProgress
                     durationSeconds={duration}
                     status={statusLabel}
                     label={t('wizard.generation.wait_hint')}
                   />
                </div>

                {/* 2) Tips Carousel - Visible during Step 1 (Macro generation) */}
                {/* Keep tips visible until we are actively generating workouts (Step 2) */}
                {!isWorkouts && (
                    <div className="w-full animate-fade-in">
                       <TipsCarousel
                         tips={selectedTips}
                         locale={i18n.language}
                       />
                    </div>
                )}

                {/* 3) Macro Preview - Visible as soon as partialPlanJson is available (usually end of Step 1) */}
                {partialPlanJson ? (
                    <div className="w-full mt-4 animate-fade-in-up">
                        <MacroPreview planJson={partialPlanJson} />
                    </div>
                ) : null}
            </div>
        );
    }

  return <div className="p-10 text-center text-gray-500">Loading Wizard...</div>;
}
